import type { EntryRow } from '@/db/types'

/**
 * Collapses adjacent identical entries into visual runs.
 *
 * Three hours of Sleep should read as one block with one label, not six
 * stamped repetitions. Because the timeline is virtualized row by row, runs
 * are expressed as per-row flags rather than merged DOM nodes: only the first
 * row of a run draws the label, and interior edges are suppressed.
 *
 * Built once per day and memoised. NEVER call this during render -- 03-ui-spec
 * section 2.3 and CLAUDE.md rule 9.
 */

export interface SlotRender {
  entry: EntryRow | null
  isRunStart: boolean
  isRunEnd: boolean
  /** Rows in this run, counted from its first row. Used to place the label. */
  runLength: number
}

/**
 * Two entries continue the same run when they would be indistinguishable to
 * the eye. A comment makes a block distinct even if everything else matches,
 * because it carries its own indicator and deserves its own label.
 */
function continuesRun(a: EntryRow, b: EntryRow): boolean {
  return (
    a.eventTypeId === b.eventTypeId &&
    a.eventObjectId === b.eventObjectId &&
    a.isPlanned === b.isPlanned &&
    a.comment === null &&
    b.comment === null
  )
}

/**
 * @param entries live entries for one day, any order
 * @param slotCount that day's slot count -- 46, 47, 48, 49 or 50
 */
export function buildDayRuns(entries: readonly EntryRow[], slotCount: number): SlotRender[] {
  const bySlot = new Array<EntryRow | null>(slotCount).fill(null)
  for (const entry of entries) {
    if (entry.slotIndex >= 0 && entry.slotIndex < slotCount) {
      bySlot[entry.slotIndex] = entry
    }
  }

  const out: SlotRender[] = new Array(slotCount)

  let index = 0
  while (index < slotCount) {
    const entry = bySlot[index] ?? null

    if (!entry) {
      out[index] = { entry: null, isRunStart: false, isRunEnd: false, runLength: 0 }
      index += 1
      continue
    }

    let end = index
    while (end + 1 < slotCount) {
      const next = bySlot[end + 1]
      if (!next || !continuesRun(entry, next)) break
      end += 1
    }

    const runLength = end - index + 1
    for (let i = index; i <= end; i++) {
      out[i] = {
        entry: bySlot[i] ?? null,
        isRunStart: i === index,
        isRunEnd: i === end,
        runLength,
      }
    }

    index = end + 1
  }

  return out
}

/**
 * Per-day cache.
 *
 * Keyed by dayKey plus a version stamp, so a data change invalidates only the
 * days that actually changed rather than the whole window.
 */
export class RunCache {
  private cache = new Map<number, { version: number; runs: SlotRender[] }>()

  get(dayKey: number, version: number): SlotRender[] | null {
    const hit = this.cache.get(dayKey)
    return hit && hit.version === version ? hit.runs : null
  }

  set(dayKey: number, version: number, runs: SlotRender[]): void {
    this.cache.set(dayKey, { version, runs })
  }

  clear(): void {
    this.cache.clear()
  }
}
